import React, { useEffect, useState, useRef } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useBrandStore } from "./store/brandStore";
import { RoutePaths } from "../src/routes/route";
import Layout from "./components/layout/layout";
import { authService } from "./services/authService";
import "./app/globals.css";

import Home from "./pages/Home";
import RFMCombination from "./pages/RFMCombination";
import { settingApi } from "./api/settingApi";
import { getToken } from "./utils/getToken";
import { Result, Spin } from "antd";
import CreateRFMProfile from "./pages/CreateRFMProfile";
import RFMProfile from "./pages/RFMProfile";
import EditRFMProfile from "./pages/EditRFMProfile";
import ViewRFMProfile from "./pages/ViewRFMProfile";
import TargetMarketingHome from "./pages/TargetMarketingHome";
import TargetMarketingAll from "./pages/TargetMarketingAll";

import CreateCampaign from "./pages/CreateCampaign";
import UpdateCampaign from "./pages/UpdateCampaign";
import DetailCampaign from "./pages/DetailCampaign";

import MessageTemplates from "./pages/MessageTemplates";
import CreateMessageTemplates from "./pages/CreateMessageTemplates";

const App: React.FC = () => {
  const { setBrandId } = useBrandStore();
  const [loading, setLoading] = useState(true);
  const [notAllowed, setNotAllowed] = useState(false);
  const location = useLocation();
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchBrandData = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;

      setLoading(true);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || "";

        const params = new URLSearchParams(window.location.search);
        const paramBrandId = params.get("brandId");
        let brandId = paramBrandId;

        const isDevelopment =
          API_BASE_URL === "" ||
          API_BASE_URL === "https://sit2-api.getzpay.com" ||
          API_BASE_URL === "https://staging-api.getzpay.com" ||
          API_BASE_URL.includes("local");

        const allowedBrandIds = new Set([
          "30fb3590-7324-4ab4-8347-586837fd8c88", // Siam Kitchen
          "845c6f6a-c325-49a5-b79d-b9df776c781e", // Tanglin Cookhouse
          "9db7ba8a-1e81-4938-93d0-5af5281e63c4", // Typhoon Cafe
          "ed2ad044-9db1-4d79-8350-58bb500ab523", // Bangkok Jam
          "8069beb5-3a1e-4b40-8e16-386ab47577ad", // Barossa
          "a18fb5fe-1e12-4ab3-9778-f7ddb353d11b", // Sarai
          "24d8afe0-d675-4342-995f-bcccad5fd218", // Suki Suki Hot Pot
          "0ee0ad64-33a8-4bdf-8788-c69d3162d1c0", // Suki-Ya
          "b6c9462b-b9b0-4c02-87d8-89fff950f668", // Tajimaya Yakiniiku
          "a4b5f491-319f-4f1f-b267-10cecfce4efe", // Vineyard
          "66d583f6-51e7-40ca-9255-99d1a0803a19", // Nakara Thai Cuisine
          "da93dec7-9762-4e74-88a4-864b71db492d", // Shiok Kitchen Catering
          "47fc47a3-6598-49c9-93b2-817c739a5de9", // Siam Square Mookata
          "f66941a1-fefb-40e7-b1fc-4bb2676e1bbe", // Sample 1A
        ]);

        const blockedBrandIds = new Set([
          "4ba8da88-1043-4132-98d0-634a40567afa", // Greendot
          "14596b2e-b183-48de-ab94-66e8eb5f9b62", // C241
        ]);

        // if (
        //   !isDevelopment &&
        //   paramBrandId &&
        //    && !allowedBrandIds.has(paramBrandId)
        //   blockedBrandIds.has(paramBrandId)
        // ) {
        //   setNotAllowed(true);
        //   return;
        // }

        const token = getToken();
        if (!token) {
          console.warn("No token found, skipping fetch.");
          return;
        }

        if (!paramBrandId) {
          const brandData = await authService.getBrandList();
          if (Array.isArray(brandData) && brandData.length > 0) {
            // const firstAllowedBrand = isDevelopment
            //   ? brandData[0]
            //   : brandData.find((brand) => !blockedBrandIds.has(brand.BrandId));

            const firstAllowedBrand = brandData[0];

            if (!firstAllowedBrand) {
              console.warn("No allowed brand found in brand list.");
              setNotAllowed(true);
              return;
            }

            brandId = firstAllowedBrand.BrandId;
            setBrandId(brandId);
          } else {
            console.warn("No brands available.");
            return;
          }
        } else {
          setBrandId(paramBrandId);
        }

        if (brandId) {
          const excludedPathsProfile = [
            RoutePaths.RFMPROFILE,
            RoutePaths.IFRAMERFMPROFILE,

            RoutePaths.CREATERFMPROFILE,
            RoutePaths.IFRAMECREATERFMPROFILE,

            RoutePaths.EDITRFMPROFILE,
            RoutePaths.IFRAMEEDITRFMPROFILE,

            RoutePaths.VIEWRFMPROFILE,
            RoutePaths.IFRAMEVIEWRFMPROFILE,
          ];
          if (!excludedPathsProfile.includes(location.pathname)) {
            await settingApi.getSetting(token, brandId);
          }
        } else {
          console.warn("No valid brandId found");
        }
      } catch (error) {
        console.error("Error fetching brand data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrandData();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");

    if (token) {
      Cookies.set("auth_token", token, { expires: 365 * 20 });
    }
  }, [location.search]);

  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Spin size="large" />
          <div style={{ marginTop: 8, color: "rgba(0,0,0,0.45)" }}>Loading data...</div>
        </div>
      </div>
    );

  if (notAllowed && !loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <Result
          status="403"
          title="Access Denied"
          subTitle={
            <>
              You are not allowed to access this RFM data. <br />
              Please contact the administrator!
            </>
          }
          style={{
            backgroundColor: "white",
            padding: "24px",
            borderRadius: "8px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        />
      </div>
    );
  }

  return <AppRoutes />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path={RoutePaths.IFRAMECOMBINATIONRFM}
        element={<RFMCombination />}
      />
      <Route path={RoutePaths.IFRAMEHOME} element={<Home />} />
      <Route path={RoutePaths.IFRAMERFMPROFILE} element={<RFMProfile />} />
      <Route
        path={RoutePaths.IFRAMECREATERFMPROFILE}
        element={<CreateRFMProfile />}
      />
      <Route
        path={RoutePaths.IFRAMEEDITRFMPROFILE}
        element={<EditRFMProfile />}
      />
      <Route
        path={RoutePaths.IFRAMEVIEWRFMPROFILE}
        element={<ViewRFMProfile />}
      />
      <Route
        path={RoutePaths.IFRAME_TARGET_MARKETING_HOME}
        element={<TargetMarketingHome />}
      />
      <Route
        path={RoutePaths.IFRAME_TARGET_MARKETING_ALL}
        element={<TargetMarketingAll />}
      />
      <Route
        path={RoutePaths.IFRAME_CREATE_CAMPAIGN}
        element={<CreateCampaign />}
      />
      <Route
        path={RoutePaths.IFRAME_UPDATE_CAMPAIGN}
        element={<UpdateCampaign />}
      />
      <Route
        path={RoutePaths.IFRAME_DETAIL_CAMPAIGN}
        element={<DetailCampaign />}
      />
      <Route
        path={RoutePaths.IFRAME_MESSAGE_TEMPLATES}
        element={<MessageTemplates />}
      />
      <Route
        path={RoutePaths.IFRAME_CREATE_MESSAGE_TEMPLATES}
        element={<CreateMessageTemplates />}
      />
      <Route element={<Layout />}>
        <Route path={RoutePaths.HOME} element={<Home />} />
        <Route path={RoutePaths.COMBINATIONRFM} element={<RFMCombination />} />
        <Route path={RoutePaths.RFMPROFILE} element={<RFMProfile />} />
        <Route path="*" element={<Navigate to={RoutePaths.HOME} />} />
        <Route
          path={RoutePaths.CREATERFMPROFILE}
          element={<CreateRFMProfile />}
        />
        <Route
          path={RoutePaths.TARGET_MARKETING_HOME}
          element={<TargetMarketingHome />}
        />
        <Route
          path={RoutePaths.TARGET_MARKETING_ALL}
          element={<TargetMarketingAll />}
        />
        <Route
          path={RoutePaths.MESSAGE_TEMPLATES}
          element={<MessageTemplates />}
        />
        <Route
          path={RoutePaths.CREATE_MESSAGE_TEMPLATES}
          element={<CreateMessageTemplates />}
        />
        <Route path={RoutePaths.CREATE_CAMPAIGN} element={<CreateCampaign />} />
        <Route path={RoutePaths.UPDATE_CAMPAIGN} element={<UpdateCampaign />} />
        <Route path={RoutePaths.DETAIL_CAMPAIGN} element={<DetailCampaign />} />
        <Route path={RoutePaths.EDITRFMPROFILE} element={<EditRFMProfile />} />
        <Route path={RoutePaths.VIEWRFMPROFILE} element={<ViewRFMProfile />} />
      </Route>
    </Routes>
  );
};

export default App;
